import { useState } from 'react';
import {
  MEETING_RESPONSE_LABEL,
  type BriefingMatch,
  type CalendarEventSummary,
  type MeetingBriefing as Briefing,
} from '@todo/shared';
import { calendarApi } from '../api/calendar.api';
import { ApiError } from '../api/client';

/**
 * The pre-meeting briefing, on demand.
 *
 * WHY IT IS A BUTTON AND NOT AUTOMATIC: a briefing costs a model call, and
 * most events opened in this panel are being edited, not walked into. It
 * is generated when someone is actually about to go.
 *
 * WHAT IS COMPUTED VS WRITTEN: the people, the counts, the dates and the
 * outstanding prep come from the database and are shown as given. Only the
 * recap, the open items and the talking points are written, and only from
 * the previous meetings' notes - which is why the raw notes stay one click
 * away underneath. Anything in the written part can be checked against
 * them.
 */

const MATCH_LABEL: Record<BriefingMatch, string> = {
  SERIES: 'same series',
  TITLE: 'same title',
  PEOPLE: 'same people',
};

function dayLabel(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

/** "met 3 times, last on 12 Sep" / "first time" - said plainly. */
function historyLabel(metBefore: number, lastMetAt: string | null): string {
  if (metBefore === 0) return 'first meeting together';
  const times = metBefore === 1 ? 'once' : `${metBefore} times`;
  return lastMetAt ? `met ${times}, last on ${dayLabel(lastMetAt)}` : `met ${times}`;
}

export function MeetingBriefingPanel({ event }: { event: CalendarEventSummary }) {
  const [brief, setBrief] = useState<Briefing | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showNotes, setShowNotes] = useState(false);

  async function run() {
    setBusy(true);
    setError(null);
    try {
      setBrief(await calendarApi.briefing(event.id));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not prepare a briefing.');
    } finally {
      setBusy(false);
    }
  }

  const past = new Date(event.endsAt).getTime() < Date.now();

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3.5">
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Briefing
        </p>
        <button
          type="button"
          onClick={() => void run()}
          disabled={busy}
          className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-40"
        >
          {busy ? 'Preparing...' : brief ? 'Refresh' : 'Brief me'}
        </button>
      </div>
      {!brief && !busy && (
        <p className="mt-0.5 text-[11px] text-slate-400">
          {past
            ? 'Who was there, and what the previous meetings recorded.'
            : 'Who you are meeting, what happened last time, and what to raise.'}
        </p>
      )}

      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}

      {brief && (
        <div className="mt-3 space-y-3">
          {/* ---- who ---- */}
          {brief.people.length > 0 ? (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                Who
              </p>
              <ul className="mt-1 space-y-1">
                {brief.people.map((p) => (
                  <li key={p.userId} className="text-sm">
                    <span className="font-medium text-slate-800">{p.displayName}</span>
                    <span className="text-slate-500">
                      {' '}· {MEETING_RESPONSE_LABEL[p.response]} ·{' '}
                      {historyLabel(p.metBefore, p.lastMetAt)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="text-xs text-slate-400">
              {brief.kind === 'ENGAGEMENT'
                ? 'A public engagement - nobody is invited.'
                : 'Nobody else is on this meeting.'}
            </p>
          )}

          {/* ---- where things stand ---- */}
          {brief.recap && (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                Where things stand
              </p>
              <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">
                {brief.recap}
              </p>
            </div>
          )}

          {brief.openItems.length > 0 && (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                Still open
              </p>
              <ul className="mt-1 space-y-0.5">
                {brief.openItems.map((item) => (
                  <li key={item} className="text-sm text-slate-700">- {item}</li>
                ))}
              </ul>
            </div>
          )}

          {brief.talkingPoints.length > 0 && (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                Worth raising
              </p>
              <ul className="mt-1 space-y-0.5">
                {brief.talkingPoints.map((point) => (
                  <li key={point} className="text-sm text-slate-700">- {point}</li>
                ))}
              </ul>
            </div>
          )}

          {brief.prepOutstanding.length > 0 && (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-700">
                Prep not done ({brief.prepOutstanding.length})
              </p>
              <ul className="mt-1 space-y-0.5">
                {brief.prepOutstanding.map((step) => (
                  <li key={step} className="text-sm text-amber-800">- {step}</li>
                ))}
              </ul>
            </div>
          )}

          {/* ---- the source ---- */}
          {brief.history.length > 0 ? (
            <div className="border-t border-slate-100 pt-2.5">
              <button
                type="button"
                onClick={() => setShowNotes((v) => !v)}
                className="flex w-full items-center justify-between gap-2 text-left"
              >
                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  {brief.history.length} previous{' '}
                  {brief.history.length === 1 ? 'meeting' : 'meetings'}
                </span>
                <span className="text-[11px] text-slate-400">
                  {showNotes ? 'Hide notes' : 'Show notes'}
                </span>
              </button>
              <ul className="mt-1.5 space-y-2">
                {brief.history.map((h) => (
                  <li key={h.eventId}>
                    <p className="text-xs text-slate-600">
                      <span className="font-medium text-slate-800">{h.title}</span>
                      {' · '}{dayLabel(h.startsAt)}
                      {' · '}
                      <span className="text-slate-400">{MATCH_LABEL[h.match]}</span>
                      {!h.notes && <span className="text-slate-400"> · no notes</span>}
                    </p>
                    {showNotes && h.notes && (
                      <p className="mt-1 whitespace-pre-wrap rounded-lg bg-slate-50 p-2.5 text-xs leading-relaxed text-slate-600">
                        {h.notes}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="border-t border-slate-100 pt-2.5 text-xs text-slate-400">
              No earlier meeting matches this one - nothing to catch up on.
            </p>
          )}

          {/* Two different silences, said differently: nobody wrote
              anything, versus the summariser is switched off. */}
          {brief.history.length > 0 && !brief.basedOnNotes && (
            <p className="rounded-lg bg-slate-50 px-2.5 py-2 text-[11px] text-slate-500">
              None of the previous meetings had notes, so there is no record
              of what was discussed - only who met and when.
            </p>
          )}
          {!brief.aiAvailable && (
            <p className="rounded-lg bg-amber-50 px-2.5 py-2 text-[11px] text-amber-800">
              The written summary is unavailable right now - the previous
              notes are above, unchanged.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
